/**
 * X25519 ECIES-like hybrid encryption for the cross-admin family-privkey
 * handoff (Phase 3). Same protocol shape as Phase 2's usageEventCrypto.ts
 * but operates on raw Uint8Array (the 32-byte family privkey) instead of
 * a JSON-encoded {itemName, siteUrl} payload, and uses a distinct HKDF
 * info label for domain separation.
 *
 * Domain separation: HKDF info "family-handoff-v1" is distinct from
 * Phase 2's "usage-event-v1" and the wrapping labels "vault-key-wrapping"
 * and "account-key-wrapping".
 *
 * decryptFamilyHandoff takes the recipient's account pubkey as a third
 * arg because Web Crypto X25519 privkeys are imported non-extractable;
 * the decrypt path can't recover the pubkey from the privkey alone. The
 * pubkey is already known on the caller's side (it's their own).
 */
import { bytesToBase64, bytesToBase64Url, base64ToBytes, base64UrlToBytes } from './encoding';

const HKDF_INFO = 'family-handoff-v1';

export interface EncryptedHandoff {
  encryptedFamilyKey: string;     // base64
  encryptedFamilyKeyIv: string;   // base64 (12 bytes)
  ephemeralPublicKey: string;     // base64url (32 bytes)
}

export async function encryptFamilyHandoff(
  familyPrivKey: Uint8Array,
  recipientAccountPub: Uint8Array,
): Promise<EncryptedHandoff> {
  const ephKeyPair = (await crypto.subtle.generateKey(
    { name: 'X25519' },
    true,
    ['deriveBits'],
  )) as CryptoKeyPair;

  const ephPubJwk = await crypto.subtle.exportKey('jwk', ephKeyPair.publicKey);
  const ephPubBytes = base64UrlToBytes(ephPubJwk.x!);

  const recipientPubKey = await crypto.subtle.importKey(
    'jwk',
    { kty: 'OKP', crv: 'X25519', x: bytesToBase64Url(recipientAccountPub) },
    { name: 'X25519' },
    true,
    [],
  );

  const sharedSecret = await crypto.subtle.deriveBits(
    { name: 'X25519', public: recipientPubKey },
    ephKeyPair.privateKey,
    256,
  );

  const salt = new Uint8Array(64);
  salt.set(ephPubBytes, 0);
  salt.set(recipientAccountPub, 32);

  const hkdfKeyMaterial = await crypto.subtle.importKey(
    'raw', sharedSecret, 'HKDF', false, ['deriveKey'],
  );
  const aesKey = await crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt, info: new TextEncoder().encode(HKDF_INFO) },
    hkdfKeyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt'],
  );

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, familyPrivKey as BufferSource),
  );

  return {
    encryptedFamilyKey: bytesToBase64(ciphertext),
    encryptedFamilyKeyIv: bytesToBase64(iv),
    ephemeralPublicKey: bytesToBase64Url(ephPubBytes),
  };
}

export async function decryptFamilyHandoff(
  handoff: EncryptedHandoff,
  recipientAccountPriv: Uint8Array,
  recipientAccountPub: Uint8Array,
): Promise<Uint8Array> {
  const recipientPrivKey = await crypto.subtle.importKey(
    'jwk',
    {
      kty: 'OKP',
      crv: 'X25519',
      x: bytesToBase64Url(recipientAccountPub),
      d: bytesToBase64Url(recipientAccountPriv),
    } as JsonWebKey,
    { name: 'X25519' },
    false,
    ['deriveBits'],
  );

  const ephPubBytes = base64UrlToBytes(handoff.ephemeralPublicKey);
  const ephPubKey = await crypto.subtle.importKey(
    'jwk',
    { kty: 'OKP', crv: 'X25519', x: handoff.ephemeralPublicKey },
    { name: 'X25519' },
    true,
    [],
  );

  const sharedSecret = await crypto.subtle.deriveBits(
    { name: 'X25519', public: ephPubKey },
    recipientPrivKey,
    256,
  );

  const salt = new Uint8Array(64);
  salt.set(ephPubBytes, 0);
  salt.set(recipientAccountPub, 32);

  const hkdfKeyMaterial = await crypto.subtle.importKey(
    'raw', sharedSecret, 'HKDF', false, ['deriveKey'],
  );
  const aesKey = await crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt, info: new TextEncoder().encode(HKDF_INFO) },
    hkdfKeyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt'],
  );

  const ivBytes = base64ToBytes(handoff.encryptedFamilyKeyIv);
  const ctBytes = base64ToBytes(handoff.encryptedFamilyKey);
  const plain = new Uint8Array(
    await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivBytes.buffer as ArrayBuffer },
      aesKey,
      ctBytes.buffer as ArrayBuffer,
    ),
  );
  return plain;
}

